import { isUnresolved } from 'src/alerts/AlertCenter.functions';
import type {
  tAlertCopy,
  tAlertDeclaration,
  tAlertPriority,
  tAlertSnapshot,
  tLiveAlert,
} from 'src/alerts/AlertCenter.types';
import { ALERT_REGISTRY } from 'src/alerts/AlertRegistry';

/**
 * AGI-888 (U3) — what happened to an alert during this session, newest last.
 * Kept by the provider (always mounted) per workspace and user, until reload:
 * a P0 that resolves while the person is on another page is recorded all the
 * same, and the feed's "last resolved" row reads it when it mounts.
 *
 * `copy` is the alert's copy as the viewer last saw it while it was a problem,
 * so the row names it by the title they read.
 */
export type tAlertSessionEntry = {
  kind: 'acknowledged' | 'resolved';
  key: string;
  id: string;
  priority: tAlertPriority;
  copy: tAlertCopy;
  /** Epoch ms of the acknowledgment or of the render that confirmed the resolution. */
  at: number;
};

/** An alert seen alive this session, with what its last sighting proved. */
export type tTrackedAlert = {
  alert: tLiveAlert;
  /** Every `dependsOn` source was `ready` when it was last seen alive. */
  sourcesReady: boolean;
};

/**
 * R3b — the declarations whose state cannot be judged yet: every one while the
 * role is unknown (the snapshot is still assembling), else those with a
 * contributing source still loading. A surface that freezes what it shows on
 * mount reads this to tell "not known yet" from "not live".
 */
export function selectLoadingAlertIds(
  snapshot: tAlertSnapshot,
  registry: tAlertDeclaration[] = ALERT_REGISTRY,
): string[] {
  if (!snapshot.role) return registry.map((declaration) => declaration.id);
  return registry
    .filter((declaration) => isUnresolved(declaration, snapshot))
    .map((declaration) => declaration.id);
}

/** Every `dependsOn` source of the declaration reads `ready` (not loading, not failed). */
export function areSourcesReady(
  declaration: tAlertDeclaration,
  snapshot: tAlertSnapshot,
): boolean {
  return (declaration.dependsOn ?? []).every((key) => {
    const source = snapshot[key] as { status?: unknown } | null | undefined;
    if (!source || typeof source !== 'object' || !('status' in source))
      return true;
    return source.status === 'ready';
  });
}

/**
 * A resolution is confirmed, never inferred from a gap: the predicate reads
 * false while every source it depends on is `ready`, both now and when the
 * alert was last seen alive. A source that went loading or failed proves
 * nothing, and neither do acknowledgment, surface scoping, exclusivity or
 * suppression: they hide an alert whose predicate still holds, and the
 * predicate is all this reads. A predicate that throws is unknown, not over.
 */
export function isResolutionConfirmed(options: {
  tracked: tTrackedAlert;
  snapshot: tAlertSnapshot;
}): boolean {
  const { tracked, snapshot } = options;
  const { declaration } = tracked.alert;
  if (!snapshot.role || !declaration.roles.includes(snapshot.role))
    return false;
  if (!tracked.sourcesReady || !areSourcesReady(declaration, snapshot))
    return false;
  try {
    return !declaration.derive(snapshot);
  } catch {
    return false;
  }
}

/**
 * Records the alerts on screen now into the tracked set. A success notice is
 * the good news itself (the 8c "connected again" card), not a problem: it
 * never opens a record, and an alert that turns into one keeps the copy it had
 * as a problem, so the row later says what was resolved (hallazgo 18).
 */
export function trackVisibleAlerts(options: {
  tracked: Map<string, tTrackedAlert>;
  alerts: tLiveAlert[];
  snapshot: tAlertSnapshot;
}): void {
  const { tracked, alerts, snapshot } = options;
  alerts.forEach((alert) => {
    const sourcesReady = areSourcesReady(alert.declaration, snapshot);
    if (alert.copy.variant === 'success') {
      const entry = tracked.get(alert.key);
      if (entry) tracked.set(alert.key, { ...entry, sourcesReady });
      return;
    }
    tracked.set(alert.key, { alert, sourcesReady });
  });
}

/** Takes out of the tracked set, and returns, the alerts whose resolution this snapshot confirms. */
export function takeConfirmedResolutions(options: {
  tracked: Map<string, tTrackedAlert>;
  snapshot: tAlertSnapshot;
  at: number;
}): tAlertSessionEntry[] {
  const { tracked, snapshot, at } = options;
  const resolved: tAlertSessionEntry[] = [];
  tracked.forEach((entry, key) => {
    if (!isResolutionConfirmed({ tracked: entry, snapshot })) return;
    tracked.delete(key);
    resolved.push({
      kind: 'resolved',
      key,
      id: entry.alert.id,
      priority: entry.alert.priority,
      copy: entry.alert.copy,
      at,
    });
  });
  return resolved;
}
